import { useMemo } from 'react';
import { useAuthContext } from '../../../auth/contexts/AuthContext';
import useGraphQLClient from '../../../../app/hooks/useGraphQLClient';
import { useQuery } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import IFundInformations from '../../interfaces/fund/informations/IFundInformations';
import AppError from '../../../../app/features/error/services/AppError';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';

const useInvestFundInformations = (fundId: number | null) => {
	const auth = useAuthContext();
	const { i18n } = useTranslation();
	const graphQLClient = useGraphQLClient();
	const filter = {
		fundId: fundId,
		userId: auth.authOverrideUserId,
	};

	const { data, isFetching } = useQuery<IFundInformations, Error>(
		['investmentFundInformations', filter, i18n.language],
		async () => {
			try {
				const data = (await graphQLClient.request(
					gql`
						query ($fundId: Int) {
							investmentFundInformations(fundId: $fundId) {
								general {
									juridicalStatus
									isin
									currency
									targetedPerformance
									fundAdminFeesNum
									fundAdminFeesText
									distributionFrequencyPerYear
									hurdle
									accreditationDate
									creationDate
									amfClassification
									risk
								}
								roles {
									fundAdministrator
									managementCompany
									consultingCompany
									depository
									accountants
								}
								strategy {
									domainTag
									domainText
									sfdr
									assetClass
									sgeThematic
									placementDuration
								}
								labels {
									ciesLabel
									finansolLabel
									isrLabel
									greenfinLabel
								}
								contacts {
									isMoral
									firstname
									lastname
									moralName
									phone
									email
									roleTag
									pictureTag
								}
								planning {
									creationDate
									subscriptionEndDate
									capitalCallEndDate
									investmentEndDate
									amortizationEndDate
									liquidationDate
									closingDates {
										position
										closingDate
									}
								}
							}
						}
					`,
					filter,
				)) as { investmentFundInformations: IFundInformations };
				return data.investmentFundInformations;
			} catch (e) {
				throw new AppError(e, 'useInvestFundInformations');
			}
		},
		{
			enabled:
				!!filter.fundId && auth.currentCompany !== null && auth.isInvestor === true && auth.isAuthenticated,
		},
	);

	const fundInformations = useMemo(() => (data ? _.cloneDeep(data) : null), [data]);

	return {
		isFetching,
		fundInformations,
	};
};

export default useInvestFundInformations;
